import React, { FC, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
  ITabs,
  MuiBox,
  MuiTabPanel,
  MuiTabs,
  WarningConfirmationModal,
} from "../../../../../../../../components";
import {
  filterListByFilterKeyValue,
  getParsedBEErrors,
  hasKey,
  isNotEmpty,
} from "../../../../../../../../helpers/utils";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { CreateClientActivitiesAndDeliverablesWrapper } from "./ProjectActivitiesAndDeliverables.style";

import {
  ActivityType,
  ProjectActivitiesAndDeliverablesProps,
  ProjectActivitiesFormKeys,
  ProjectActivitiesRowValue,
  ProjectActivityAndDeliverablesFiltersTypes,
  ProjectDeliverablesFormKeys,
  ProjectDeliverablesRowValue,
  ProjectDeliverableUniqueFiltersTypes,
} from "./modelsAndSchemas/models";

import { useDispatch, useSelector } from "react-redux";
import {
  BE_ERROR_CODES,
  MODULE_PERMISSIONS,
  PAGE_IDS,
} from "../../../../../../../../helpers/constants";
import { UnitTypesKey } from "../../../../../../../../helpers/constants/siteCoreKeys";
import { siteCoreContentLoadingSelector } from "../../../../../../../../redux/slices/appState/appStateSelector";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../../../redux/slices/appState/appStateSlice";

import { ErrorModel } from "../../../../../../../../api/services/client/models";
import {
  useDeleteProjectActivityMutation,
  useDeleteProjectDeliverableMutation,
} from "../../../../../../../../api/services/project/projectApis";
import { ButtonWithIcon } from "../../../../../../../../components/buttonWithIcon/ButtonWithIcon";
import { LabelValue } from "../../../../../../../../helpers/types/common";
import { usePermissions } from "../../../../../../../../hooks/usePermissions";
import { ProjectEditActivity } from "./components/editActivity/ProjectEditActivity";
import { ProjectEditDeliverable } from "./components/editDeliverable/ProjectEditDeliverable";
import { ProjectViewActivity } from "./components/viewActivity/ProjectViewActivity";
import { ProjectViewDeliverables } from "./components/viewDeliverables/ProjectViewDeliverables";
import {
  activitiesListSiteCoreKeys,
  addActivitySiteCoreKeys,
  deliverablesListSiteCoreKeys,
} from "./siteCore/SiteCoreKeys";
import { ProjectActivitiesTab } from "./tabs/ProjectActivitiesTab";
import { ProjectDeliverablesTab } from "./tabs/ProjectDeliverablesTab";

export const pageId = PAGE_IDS.CREATE_CLIENT_ACTIVITY_DELIVERABLES;

export const CreateClientActivitiesAndDeliverables: FC<
  ProjectActivitiesAndDeliverablesProps
> = ({
  rows,
  deliverableRows,
  onCreateActivityClick,
  successHandler,
  successHandlerDeliverables,
  isView = false,
  onCreateDeliverableClick,
}) => {
  const tableRef = useRef<{ resetPagination: () => void }>(null);
  const deliverableTableRef = useRef<{ resetPagination: () => void }>(null);
  const [showViewProjectActivity, setShowViewProjectActivity] = useState(false);
  const [showEditProjectActivity, setShowEditProjectActivity] = useState(false);
  const [showEditProjectDeliverables, setShowEditProjectDeliverables] =
    useState(false);
  const [showViewProjectDeliverables, setShowViewProjectDeliverables] =
    useState(false);
  const [selectedProjectActivity, setSelectedProjectActivity] = useState(
    {} as ProjectActivitiesRowValue,
  );
  const [selectedProjectDeliverables, setSelectedProjectDeliverables] =
    useState({} as ProjectDeliverablesRowValue);

  const { fetchLabel, commonLabels, fetchValueByKeyOfDropdown } = useSiteCore();
  const { hasModulePermission } = usePermissions();
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  // updated sitecore data
  const [allUpdatedActivitiesRows, setAllUpdatedActivitiesRows] = useState<
    ProjectActivitiesRowValue[]
  >([]);

  const [
    allUpdatedProjectDeliverablesRows,
    setAllUpdatedProjectDeliverablesRows,
  ] = useState<ProjectDeliverablesRowValue[]>([]);
  const [filteredResults, setFilteredResults] = useState([...rows]);
  const [
    filteredProjectDeliverableResults,
    setFilteredProjectDeliverableResults,
  ] = useState([...deliverableRows]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showConfirmationModalFor, setConfirmationShowModalFor] = useState("");
  const [
    showWarningAlreadyInUseProjectActivity,
    setShowWarningAlreadyInUseProjectActivity,
  ] = useState(false);

  const [
    activityAndDeliverablesUniqueFilters,
    setActivityAndDeliverablesUniqueFilters,
  ] = useState<ProjectActivityAndDeliverablesFiltersTypes>({
    [ProjectActivitiesFormKeys.activityType]: [],
    [ProjectActivitiesFormKeys.modality]: [],
    [ProjectActivitiesFormKeys.units]: [],
    [ProjectActivitiesFormKeys.projectFramework]: [],
  });

  const [uniqueDeliverablesFilters, setUniqueDeliverablesFilters] =
    useState<ProjectDeliverableUniqueFiltersTypes>({
      [ProjectDeliverablesFormKeys.activityTypes]: [],
      [ProjectDeliverablesFormKeys.deliverableType]: [],
      [ProjectDeliverablesFormKeys.units]: [],
      [ProjectDeliverablesFormKeys.activityType]: [],
    });

  const dispatch = useDispatch();

  const {
    mutateAsync: deleteProjectActivity,
    isSuccess,
    isPending: isDeleteProjectActivityPending,
    isError: isDeleteProjectActivityError,
    error: deleteProjectActivityError,
  } = useDeleteProjectActivityMutation();

  const {
    mutateAsync: deleteProjectDeliverable,
    isSuccess: isSuccessDeleteProjectDeliverable,
    isPending: isDeleteProjectDeliverablePending,
    isError: isDeleteProjectDeliverableError,
  } = useDeleteProjectDeliverableMutation();

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isDeleteProjectActivityPending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isDeleteProjectActivityPending]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isDeleteProjectDeliverablePending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isDeleteProjectDeliverablePending]);

  useEffect(() => {
    if (isSuccess) {
      dispatch(
        toggleToast({
          open: true,
          message: fetchLabel(
            activitiesListSiteCoreKeys.activitiesList,
            activitiesListSiteCoreKeys.deleteActivityCompletionText,
          ),
        }),
      );
      successHandler();
    }
  }, [isSuccess]);

  const showErrorsFromBE = (error: ErrorModel) => {
    if (
      error.errorCode &&
      error.errorCode ===
        BE_ERROR_CODES.ACTIVITY_ALREADY_IN_USE_PROJECT_SPECIFIC_ACTIVITY
    ) {
      setShowWarningAlreadyInUseProjectActivity(true);
    }
  };

  useEffect(() => {
    dispatch(toggleLoader({ isVisible: false }));
    if (isDeleteProjectActivityError && deleteProjectActivityError) {
      const error = getParsedBEErrors(deleteProjectActivityError)[0];
      if (isNotEmpty(error) && hasKey(error, "errorCode")) {
        showErrorsFromBE(error);
      } else {
        dispatch(
          toggleToast({
            open: true,
            message: commonLabels.someThingWentWrongPleaseTryAgainLater,
            type: "error",
          }),
        );
      }
    }
  }, [isDeleteProjectActivityError]);

  useEffect(() => {
    if (isSuccessDeleteProjectDeliverable) {
      dispatch(
        toggleToast({
          open: true,
          message: fetchLabel(
            deliverablesListSiteCoreKeys.deliverablesList,
            deliverablesListSiteCoreKeys.deleteDeliverableSuccessText,
          ),
        }),
      );
      successHandlerDeliverables();
    }
    if (isDeleteProjectDeliverableError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isSuccessDeleteProjectDeliverable, isDeleteProjectDeliverableError]);

  const handleWarningPopup = () => {
    if (showWarningAlreadyInUseProjectActivity) {
      setShowWarningAlreadyInUseProjectActivity(false);
    }
  };

  const resetProjectActivitiesPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };
  const resetProjectDeliverablesPagination = () => {
    if (deliverableTableRef.current) {
      deliverableTableRef.current.resetPagination();
    }
  };

  const indexRef = useRef<number>();

  const getValueByKeyOfDropdown = (
    masterKey: string,
    parentKey: string,
    key: string,
  ) => {
    return fetchValueByKeyOfDropdown(masterKey, parentKey, key);
  };

  const getActivityUniqueFilters = (
    data: ProjectActivitiesRowValue[],
    key: string,
  ): LabelValue[] => {
    return Array.from(
      new Set(
        data
          .filter((item: ProjectActivitiesRowValue) => item[key]) // Filter out null or undefined values
          .map((item: ProjectActivitiesRowValue) => item[key]) // Extract values for the given key
          .reduce((acc, val) => acc.concat(val), []), // Flatten the array if values are arrays
      ),
    ).map((value) => ({ value: value as string, label: value as string })); // Format as value-label pairs
  };

  const getUniqueDeliverablesFilters = (
    data: ProjectDeliverablesRowValue[],
    key: string,
  ): LabelValue[] => {
    return Array.from(
      new Set(
        data
          .filter((item: ProjectDeliverablesRowValue) => item[key]) // Filter out null or undefined values
          .map((item: ProjectDeliverablesRowValue) => item[key]) // Extract values for the given key
          .reduce((acc, val) => acc.concat(val), []), // Flatten the array if values are arrays
      ),
    ).map((value) => ({ value: value as string, label: value as string })); // Format as value-label pairs
  };

  const { control, watch, reset, getValues } = useForm({
    mode: "all",
    defaultValues: {
      [ProjectActivitiesFormKeys.activityType]: "",
      [ProjectActivitiesFormKeys.modality]: "",
      [ProjectActivitiesFormKeys.topic]: "",
      [ProjectActivitiesFormKeys.activityStatus]: "",
      [ProjectActivitiesFormKeys.units]: "",
      [ProjectActivitiesFormKeys.measurement]: "",
      [ProjectActivitiesFormKeys.projectFramework]: "",
    },
  });

  const {
    control: deliverableControl,
    watch: deliverableWatch,
    reset: deliverableReset,
    getValues: deliverableGetValues,
  } = useForm({
    mode: "all",
    defaultValues: {
      [ProjectDeliverablesFormKeys.activityTypes]: "",
      [ProjectDeliverablesFormKeys.name]: "",
      [ProjectDeliverablesFormKeys.deliverableType]: "",
      [ProjectDeliverablesFormKeys.units]: "",
      [ProjectDeliverablesFormKeys.measurements]: "",
      [ProjectDeliverablesFormKeys.activityType]: "",
    },
  });

  const deliverableDropdownKeyMapping = {
    deliverableType: deliverablesListSiteCoreKeys.deliverableTypeList,
    measurement: deliverablesListSiteCoreKeys.minutesList,
    units: deliverablesListSiteCoreKeys.unitsList,
  };

  const getDeliverablesDropdownValueForKey = (
    key: keyof typeof deliverableDropdownKeyMapping,
    data: ProjectDeliverablesRowValue,
  ) => {
    if (key === ProjectDeliverablesFormKeys.measurements) {
      if (data.units === UnitTypesKey.Minutes) {
        return Number(
          getValueByKeyOfDropdown(
            deliverablesListSiteCoreKeys.deliverablesList,
            deliverableDropdownKeyMapping[key],
            data[key],
          ),
        );
      } else {
        return Number(data[key]);
      }
    }

    return getValueByKeyOfDropdown(
      deliverablesListSiteCoreKeys.deliverablesList,
      deliverableDropdownKeyMapping[key],
      data[key],
    );
  };

  useEffect(() => {
    if (!siteCoreLoading) {
      setFilteredResults([...rows]);
      setAllUpdatedActivitiesRows(rows);

      setActivityAndDeliverablesUniqueFilters((prevFilters) => ({
        ...prevFilters,
        [ProjectActivitiesFormKeys.activityType]: getActivityUniqueFilters(
          rows,
          ProjectActivitiesFormKeys.activityType,
        ),
        [ProjectActivitiesFormKeys.modality]: getActivityUniqueFilters(
          rows,
          ProjectActivitiesFormKeys.modality,
        ),
        [ProjectActivitiesFormKeys.units]: getActivityUniqueFilters(
          rows,
          ProjectActivitiesFormKeys.units,
        ),
        [ProjectActivitiesFormKeys.projectFramework]: getActivityUniqueFilters(
          rows,
          ProjectActivitiesFormKeys.projectFramework,
        ),
      }));

      reset();
      resetProjectActivitiesPagination();
    }
  }, [rows, siteCoreLoading]);

  useEffect(() => {
    if (!siteCoreLoading) {
      const localData: ProjectDeliverablesRowValue[] = JSON.parse(
        JSON.stringify(deliverableRows),
      );

      localData.forEach((data) => {
        Object.keys(deliverableDropdownKeyMapping).forEach((key) => {
          data[key] = getDeliverablesDropdownValueForKey(
            key as keyof typeof deliverableDropdownKeyMapping,
            data,
          );
        });
      });
      setFilteredProjectDeliverableResults([...localData]);
      setAllUpdatedProjectDeliverablesRows(localData);

      const findUniqueValues = (
        data: ProjectDeliverablesRowValue[],
        column: string,
      ): any => {
        const uniqueRecords = data
          .filter((item) => item[column]?.length > 0)
          .flatMap((item) =>
            item[column].map((activityType: ActivityType) => activityType.name),
          );

        const resultSet = [...new Set(uniqueRecords)].map((value) => ({
          value,
          label: fetchValueByKeyOfDropdown(
            addActivitySiteCoreKeys.addActivity,
            addActivitySiteCoreKeys.activityTypeList,
            value,
          ),
        }));

        return resultSet;
      };

      setUniqueDeliverablesFilters((prevFilters) => ({
        ...prevFilters,
        [ProjectDeliverablesFormKeys.activityTypes]: findUniqueValues(
          localData,
          ProjectDeliverablesFormKeys.activityTypes,
        ),
        [ProjectDeliverablesFormKeys.deliverableType]:
          getUniqueDeliverablesFilters(
            localData,
            ProjectDeliverablesFormKeys.deliverableType,
          ),
        [ProjectDeliverablesFormKeys.name]: getUniqueDeliverablesFilters(
          localData,
          ProjectDeliverablesFormKeys.name,
        ),
        [ProjectActivitiesFormKeys.units]: getUniqueDeliverablesFilters(
          localData,
          ProjectActivitiesFormKeys.units,
        ),
        [ProjectActivitiesFormKeys.activityType]: getUniqueDeliverablesFilters(
          localData,
          ProjectActivitiesFormKeys.activityType,
        ),
      }));

      deliverableReset();
      resetProjectDeliverablesPagination();
    }
  }, [deliverableRows, siteCoreLoading]);

  const applyFilters = (data: ProjectActivitiesRowValue[]) => {
    const filters = getValues();
    return filterListByFilterKeyValue(
      data,
      filters,
    ) as ProjectActivitiesRowValue[];
  };

  const applyDeliverablesFilters = (data: ProjectDeliverablesRowValue[]) => {
    const filters = deliverableGetValues();

    return data.filter((item) => {
      return Object.keys(filters).every((key) => {
        const filterValue = filters[key as keyof typeof filters];

        const filterValueStr = filterValue.toString().toLowerCase();

        if (item[key] === undefined && filterValue !== "") {
          return false;
        }

        // Skip empty filters
        if (filterValue === "") return true;

        if (key === ProjectDeliverablesFormKeys.activityTypes) {
          return item[key]?.some((activityType: ActivityType) =>
            activityType.name?.toLowerCase().includes(filterValueStr),
          );
        } else {
          const itemValue = item[key]?.toString().toLowerCase();
          return itemValue?.includes(filterValueStr);
        }
      });
    });
  };

  useEffect(() => {
    if (isNotEmpty(allUpdatedActivitiesRows)) {
      const filteredData = applyFilters(allUpdatedActivitiesRows);
      setFilteredResults(filteredData);
      resetProjectActivitiesPagination();
    }
  }, [
    watch(ProjectActivitiesFormKeys.activityType),
    watch(ProjectActivitiesFormKeys.modality),
    watch(ProjectActivitiesFormKeys.topic),
    watch(ProjectActivitiesFormKeys.activityStatus),
    watch(ProjectActivitiesFormKeys.units),
    watch(ProjectActivitiesFormKeys.projectFramework),
  ]);

  useEffect(() => {
    if (isNotEmpty(allUpdatedProjectDeliverablesRows)) {
      const filteredData = applyDeliverablesFilters(
        allUpdatedProjectDeliverablesRows,
      );
      setFilteredProjectDeliverableResults(filteredData);
      resetProjectDeliverablesPagination();
    }
  }, [
    deliverableWatch(ProjectDeliverablesFormKeys.name),
    deliverableWatch(ProjectDeliverablesFormKeys.deliverableType),
    deliverableWatch(ProjectDeliverablesFormKeys.units),
    deliverableWatch(ProjectDeliverablesFormKeys.activityTypes),
    deliverableWatch(ProjectDeliverablesFormKeys.activityType),
  ]);

  const onProjectActivityViewClick = (
    data: ProjectActivitiesRowValue,
    isEdit = false,
  ) => {
    setSelectedProjectActivity(data);

    isEdit
      ? setShowEditProjectActivity(true)
      : setShowViewProjectActivity(true);
  };

  const onClickDeliverableView = (
    data: ProjectDeliverablesRowValue,
    isEdit = false,
  ) => {
    setSelectedProjectDeliverables(data);
    isEdit
      ? setShowEditProjectDeliverables(true)
      : setShowViewProjectDeliverables(true);
  };

  const [selectedTab, setSelectedTab] = useState(0);

  const tabIdProjectActivity = `${pageId}-tab1`;
  const tabIdProjectDeliverables = `${pageId}-tab2`;
  const tabs: ITabs[] = [
    {
      id: tabIdProjectActivity,
      label: fetchLabel(
        activitiesListSiteCoreKeys.activitiesList,
        activitiesListSiteCoreKeys.activitiesTabText,
      ),
    },
    {
      id: tabIdProjectDeliverables,
      label: fetchLabel(
        activitiesListSiteCoreKeys.activitiesList,
        activitiesListSiteCoreKeys.deliverablesTabText,
      ),
    },
  ];

  const onEditDeliverableClick = () => {
    setShowViewProjectDeliverables(false);
    setShowEditProjectDeliverables(true);
  };

  const onTabChange = (event: React.ChangeEvent<{}>, value: number) => {
    setSelectedTab(value);
  };

  const handleModalClose = () => {
    if (showConfirmationModal) {
      setShowConfirmationModal(false);
      setConfirmationShowModalFor("");
    }
  };

  const onClickDelete = () => {
    if (isNotEmpty(indexRef.current)) {
      let idToDelete = indexRef.current;

      if (showConfirmationModalFor === "Activity") {
        deleteProjectActivity({
          topicId: idToDelete,
        });
      } else if (showConfirmationModalFor === "Deliverable") {
        deleteProjectDeliverable({
          deliverableId: idToDelete,
        });
      }

      setShowConfirmationModal(false);
      setConfirmationShowModalFor("");
    }
  };

  const deleteProjectActivityAction = (row: any) => {
    if (showConfirmationModal === false) {
      indexRef.current = row?.id;
      setShowConfirmationModal(true);
      setConfirmationShowModalFor("Activity");
    }
  };

  const deleteProjectDeliverableAction = (row: any) => {
    if (showConfirmationModal === false) {
      indexRef.current = row?.id;
      setShowConfirmationModal(true);
      setConfirmationShowModalFor("Deliverable");
    }
  };

  const onEditActivityClick = () => {
    setShowViewProjectActivity(false);
    setShowEditProjectActivity(true);
  };

  const errorWarning = () => {
    if (showConfirmationModalFor === "Activity") {
      return fetchLabel(
        activitiesListSiteCoreKeys.activitiesList,
        activitiesListSiteCoreKeys.deleteActivityActionText,
      );
    } else if (showConfirmationModalFor === "Deliverable") {
      return fetchLabel(
        deliverablesListSiteCoreKeys.deliverablesList,
        deliverablesListSiteCoreKeys.deleteDeliverableActionText,
      );
    }
    return "";
  };

  return (
    <CreateClientActivitiesAndDeliverablesWrapper>
      <MuiTabs
        id="activities-deliverables"
        tabs={tabs}
        value={selectedTab}
        onChange={onTabChange}
        tabsTestIds={[tabIdProjectActivity, tabIdProjectDeliverables]}
      >
        <MuiTabPanel
          panelId={`${pageId}-panel1`}
          value={selectedTab}
          index={0}
          ariaLabelledById={tabIdProjectActivity}
        >
          {!isView &&
            hasModulePermission(
              MODULE_PERMISSIONS.PROJECT_ADD_ACTIVITIES_AND_DELIVERABLES,
            ) && (
              <MuiBox className="semi-header">
                <ButtonWithIcon
                  data-testid="create-activity"
                  buttonText={fetchLabel(
                    activitiesListSiteCoreKeys.activitiesList,
                    activitiesListSiteCoreKeys.addActivityButton,
                  )}
                  onActionClick={onCreateActivityClick}
                />
              </MuiBox>
            )}

          <ProjectActivitiesTab
            tableRef={tableRef}
            control={control}
            isView={isView}
            onProjectActivityViewClick={onProjectActivityViewClick}
            filteredResults={filteredResults}
            rows={rows}
            uniqueFilters={activityAndDeliverablesUniqueFilters}
            deleteActivityOrDeliverableAction={deleteProjectActivityAction}
          ></ProjectActivitiesTab>
        </MuiTabPanel>

        <MuiTabPanel
          panelId={`${pageId}-panel2`}
          value={selectedTab}
          index={1}
          ariaLabelledById={tabIdProjectDeliverables}
        >
          {!isView && (
            <MuiBox className="semi-header">
              <ButtonWithIcon
                data-testid="create-deliverables"
                buttonText={fetchLabel(
                  deliverablesListSiteCoreKeys.deliverablesList,
                  deliverablesListSiteCoreKeys.addDeliverablesLinkText,
                )}
                onActionClick={onCreateDeliverableClick}
              />
            </MuiBox>
          )}
          <ProjectDeliverablesTab
            tableRef={deliverableTableRef}
            control={deliverableControl}
            isView={isView}
            onProjectDeliverableViewClick={onClickDeliverableView}
            filteredResults={filteredProjectDeliverableResults}
            rows={deliverableRows}
            uniqueFilters={uniqueDeliverablesFilters}
            deleteAction={deleteProjectDeliverableAction}
          ></ProjectDeliverablesTab>
        </MuiTabPanel>
      </MuiTabs>

      {showViewProjectActivity && (
        <ProjectViewActivity
          show={showViewProjectActivity}
          selectedProjectActivity={selectedProjectActivity}
          handleCloseModal={() => setShowViewProjectActivity(false)}
          isView={isView}
          onClickEditButton={onEditActivityClick}
        />
      )}
      {showEditProjectActivity && (
        <ProjectEditActivity
          visible={showEditProjectActivity}
          selectedProjectActivity={selectedProjectActivity}
          handleCloseModal={() => setShowEditProjectActivity(false)}
          pageId={pageId}
          getListOfProjectActivitiesApi={successHandler}
        />
      )}

      {showViewProjectDeliverables && (
        <ProjectViewDeliverables
          visible={showViewProjectDeliverables}
          pageId={pageId}
          onCloseModal={() => setShowViewProjectDeliverables(false)}
          onEditButtonClick={() => onEditDeliverableClick()}
          selectedProjectDeliverableId={selectedProjectDeliverables?.id}
          hideEditButton={isView}
        />
      )}
      {showEditProjectDeliverables && (
        <ProjectEditDeliverable
          visible={showEditProjectDeliverables}
          pageId={pageId}
          handleCloseModal={() => setShowEditProjectDeliverables(false)}
          selectedDeliverables={selectedProjectDeliverables}
          callApiFunction={successHandlerDeliverables}
        />
      )}

      {showConfirmationModal && (
        <WarningConfirmationModal
          show={showConfirmationModal}
          modalTitle={commonLabels.confirmation}
          onClose={handleModalClose}
          onPrimaryButtonClick={onClickDelete}
          labelContent={errorWarning()}
          secondaryButtonTitle={commonLabels.no}
        />
      )}
      {showWarningAlreadyInUseProjectActivity && (
        <WarningConfirmationModal
          show={showWarningAlreadyInUseProjectActivity}
          onClose={() => {
            setShowWarningAlreadyInUseProjectActivity(false);
          }}
          onPrimaryButtonClick={handleWarningPopup}
          showSecondaryButton={false}
          primaryButtonTitle={commonLabels.ok}
          labelContent={fetchLabel(
            activitiesListSiteCoreKeys.activitiesList,
            activitiesListSiteCoreKeys.activityCannotBeDeleted,
          )}
        />
      )}
    </CreateClientActivitiesAndDeliverablesWrapper>
  );
};
